import React, { useEffect, useState } from "react";
import { Badge, Button, Row, Col, Table } from "react-bootstrap";
import { CSVLink } from "react-csv";
import {
  DatatableWrapper,
  TableBody,
  TableHeader,
  PaginationOptions,
  Pagination,
  Filter,
} from "react-bs-datatable";
import { ToastContainer } from "react-toastify";
import AsyncSelect from "react-select/async";
import { getEmployeeSkillByEmail, getEmployeeSkills } from "../../../../services/EmployeeSkillsService";
import { getSkills } from "../../../../services/SkillService";
import { getCategories } from "../../../../services/CategoryService";
import { getExpertise } from "../../../../services/ExpertiseService";
import { useAuth } from "../../../../context/authContext";
import PageTitle from "../../../layouts/PageTitle";

const SkillsDashboard = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedExpertise, setSelectedExpertise] = useState([]);
  const { user } = useAuth();
  const [showFullDashboard, setShowFullDashboard] = useState(false);
  useEffect(() => {
    const fetchData = async () => {
      try {
        if(showFullDashboard == true){
        const res = await getEmployeeSkills();
        const filteredEmployees = res.map((emp) => ({
          ...emp,
          skills: emp.skills.filter((skill) => (skill.expertise !== "none" && skill.expertise.trim().toLowerCase() !== 'no other skills')),
        }));
        setData(filteredEmployees);
        setFilteredData(filteredEmployees);
      }else{
        const res = await getEmployeeSkillByEmail(user.email);
        const filteredEmployees = res.map((emp) => ({
          ...emp,
          skills: emp.skills.filter((skill) => (skill.expertise !== "none" && skill.expertise.trim().toLowerCase() !== 'no other skills')),
        }));
        setData(filteredEmployees);
        setFilteredData(filteredEmployees);
      }
      } catch (error) {
        console.error("Error fetching skills data:", error);
      }
    };
    fetchData();
  }, [showFullDashboard]);

  

  useEffect(() => {
    const dashboardModule = user?.allowedModules?.find(module => module.value === "skills-dashboard");
    if (dashboardModule && dashboardModule.operations.includes("fulldashboard")) {
      setShowFullDashboard(true);
    } else {
      setShowFullDashboard(false);
    }
}, []);

  const [selectedEmployees, setSelectedEmployees] = useState([]);

  // Load Employees for Multi-Select
  const loadEmployees = async (inputValue) => {
    try {
      const res = await getEmployeeSkills();
      return res
        .filter((emp) =>
          emp.name.toLowerCase().includes(inputValue.toLowerCase())
        )
        .map((emp) => ({ label: emp.name, value: emp.name }));
    } catch (error) {
      console.error("Error fetching employees:", error);
      return [];
    }
  };

  // Load Skills for Multi-Select
  const loadSkills = async (inputValue) => {
    try {
      const res = await getSkills();
      return res
        .filter((skill) =>
          skill.name.toLowerCase().includes(inputValue.toLowerCase())
        )
        .map((skill) => ({ label: skill.name, value: skill.name }));
    } catch (error) {
      console.error("Error fetching skills:", error);
      return [];
    }
  };

  // Load Categories for Multi-Select
  const loadCategories = async (inputValue) => {
    try {
      const res = await getCategories();
      return res
        .filter((category) =>
          category.name.toLowerCase().includes(inputValue.toLowerCase())
        )
        .map((category) => ({ label: category.name, value: category.name }));
    } catch (error) {
      console.error("Error fetching categories:", error);
      return [];
    }
  };

  // Load Expertise for Multi-Select
  const loadExpertise = async (inputValue) => {
    try {
      const res = await getExpertise()
      return res
        .filter((expertise) =>
          expertise.name.toLowerCase().includes(inputValue.toLowerCase())
        )
        .map((expertise) => ({ label: expertise.name, value: expertise.name }));
    } catch (error) {
      console.error("Error fetching expertise:", error);
      return [];
    }
  };

  const handleFilter = () => {
    let filtered = data;

    filtered = filtered.filter(
      (emp) =>
        (selectedEmployees.length === 0 ||
          selectedEmployees.some((e) => e.value === emp.name)) &&
        emp.skills.some(
          (skill) =>
            (selectedSkills.length === 0 ||
              selectedSkills.some((s) => s.value === skill.name)) &&
            (selectedCategories.length === 0 ||
              selectedCategories.some((c) => c.value === skill.category)) &&
            (selectedExpertise.length === 0 ||
              selectedExpertise.some((e) => e.value === skill.expertise))
        )
    );

    setFilteredData(filtered);
  };

  // Define Table Columns
  const columns = [
    { prop: "name", title: "Employee Name" },
    { prop: "email", title: "Email" },
    {
      prop: "skills",
      title: "Skills",
      cell: (row) => (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "5px",
            whiteSpace: "normal",
          }}
        >
          {row.skills.length > 0
            ? row.skills.map((s) => (
                <Badge key={s.name} bg="info" className="me-1">
                  {s.name} ({s.expertise})
                </Badge>
              ))
            : "None"}
        </div>
      ),
    },
  ];

  return (
    <div className="mb-5">
      <ToastContainer />
      <PageTitle pageTitle={"Skill Dashboard"}/>

      {/* Filters */}
      <Row className="mb-3">
      {showFullDashboard == true && (
        <Col>
          <AsyncSelect
            isMulti
            cacheOptions
            defaultOptions
            loadOptions={loadEmployees}
            onChange={setSelectedEmployees}
            placeholder="Filter by Employee..."
          />
        </Col>)}
        <Col>
          <AsyncSelect
            isMulti
            cacheOptions
            defaultOptions
            loadOptions={loadSkills}
            onChange={setSelectedSkills}
            placeholder="Filter by Skills..."
          />
        </Col>
        <Col>
          <AsyncSelect
            isMulti
            cacheOptions
            defaultOptions
            loadOptions={loadCategories}
            onChange={setSelectedCategories}
            placeholder="Filter by Categories..."
          />
        </Col>
        <Col>
          <AsyncSelect
            isMulti
            cacheOptions
            defaultOptions
            loadOptions={loadExpertise}
            onChange={setSelectedExpertise}
            placeholder="Filter by Expertise..."
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col className="d-flex justify-content-end">
          <Button variant="primary" style={{"marginRight": "10px"}} onClick={handleFilter}>
            Apply Filters
          </Button>
          <CSVLink
            data={filteredData.map((emp) => ({
              Name: emp.name,
              Email: emp.email,
              "Employee Id": emp.employeeNumber,
              skills: emp.skills
                .map((skill) => skill.name + " (" + skill.expertise + ") ")
                .join(", "),
            }))}
            filename="filtered_employees.csv"
            className="btn btn-success"
          >
            Export CSV
          </CSVLink>
        </Col>
       
      </Row>

      {/* DataTable */}
      <DatatableWrapper
        body={filteredData}
        headers={columns}
        paginationOptionsProps={{
          initialState: {
            rowsPerPage: 10,
            options: [5, 10, 15],
          },
        }}
      >
        <Row>
          <Col
            xs={12}
            sm={6}
            lg={4}
            className="d-flex justify-content-lg-center align-items-center justify-content-sm-start mb-2 mb-sm-0"
          >
            <Filter />
          </Col>
        </Row>

        <Table striped bordered hover>
          <TableHeader />
          <TableBody />
        </Table>

        {/* Pagination */}
        <Row>
          <Col className="d-flex justify-content-start">
            <PaginationOptions />
          </Col>
          <Col className="d-flex justify-content-end mt-4">
            <Pagination />
          </Col>
        </Row>
      </DatatableWrapper>
    </div>
  );
};

export default SkillsDashboard;
